import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Hourglass } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { getTaskStatusColor } from '@/features/new-tasks/statusStyles';
import type { TaskStatus } from '@/features/new-tasks/types';
import { useLawyerDeadlines } from '../hooks/useLawyerDeadlines';
import { MyMattersCard } from './MyMattersCard';
import { MyMeetingsCard } from './MyMeetingsCard';

/**
 * The personal ("base") dashboard blocks of TS Lot 2 §3.1.1 — my matters,
 * meetings, deadlines.
 *
 * §3.1.3 ("Dashboard for Partner/MD") adds three governance blocks. On the
 * wording of the TS that is an **overlay** on §3.1.1, not a substitute for it,
 * which is the reading the client set out in finding D-01 of AL-2026/08-04-01:
 * an MD is also a lawyer with their own matters, hearings and deadlines.
 *
 * These blocks were built for the Lawyer dashboard and are extracted here so
 * both dashboards mount the same components rather than growing a second copy.
 * Every block is individually hideable, so an MD who only wants the governance
 * view can switch them off.
 */

function formatDueDate(iso: string) {
  const d = new Date(iso);
  const mon = d.toLocaleString('en-US', { month: 'short' });
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mon} ${dd}`;
}

function formatDueTime(iso: string) {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${min}`;
}

function LeftBadge({ days }: { days: number }) {
  const { t } = useTranslation('common');
  let cls: string;
  let label: string;
  if (days < 0) {
    cls = 'bg-red-50 text-red-700 border-red-100';
    label = t('dashboard.daysOverdue', { days: Math.abs(days) });
  } else if (days === 0) {
    cls = 'bg-amber-50 text-amber-700 border-amber-200';
    label = t('dashboard.dueToday');
  } else if (days <= 3) {
    // §3.1.1 — under three days is red.
    cls = 'bg-red-50 text-red-700 border-red-100';
    label = t('dashboard.daysLeft', { days });
  } else if (days <= 7) {
    cls = 'bg-amber-50 text-amber-700 border-amber-200';
    label = t('dashboard.daysLeft', { days });
  } else {
    cls = 'bg-green-50 text-green-700 border-green-100';
    label = t('dashboard.daysLeft', { days });
  }
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[0.75rem] font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

function LoadingRows({ cols, rows = 3 }: { cols: number; rows?: number }) {
  return (
    <>
      {[...Array(rows)].map((_, i) => (
        <tr key={i} className="border-b border-slate-100 last:border-b-0">
          {[...Array(cols)].map((__, j) => (
            <td key={j} className="px-2 py-2.5">
              <div className="h-4 animate-pulse rounded bg-slate-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * My deadlines — the signed-in user's own actionable tasks (draft and
 * lead-rejected), nearest deadline first. A row click opens My Tasks filtered
 * to that task.
 */
export function MyDeadlinesCard() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data: deadlines, isLoading } = useLawyerDeadlines();

  const open = (id: number) => navigate(`/app/my-tasks?id=${id}`);

  return (
    <Card>
      <CardHeader className="border-b border-slate-100 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Hourglass size={16} className="text-slate-400" />{' '}
          {t('dashboard.deadlines')}
        </CardTitle>
        <CardDescription>{t('dashboard.deadlinesDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {[
                  'ID',
                  t('status.status'),
                  t('description'),
                  t('dashboard.matter'),
                  t('client'),
                  t('dueDate'),
                  t('dashboard.left'),
                ].map((h) => (
                  <th
                    key={h}
                    className="whitespace-nowrap px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <LoadingRows cols={7} />
              ) : (deadlines ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-2 py-8 text-center text-slate-400">
                    {t('dashboard.noDeadlines')}
                  </td>
                </tr>
              ) : (
                (deadlines ?? []).map((row) => (
                  <tr
                    key={row?.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => open(row.id)}
                    onKeyDown={(e) =>
                      (e.key === 'Enter' || e.key === ' ') && open(row.id)
                    }
                    className="cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                  >
                    <td className="px-2 py-2 text-[0.8125rem] font-bold tabular-nums text-slate-500">
                      {row?.id}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">
                      {row?.newStatus ? (
                        <Badge
                          className={`rounded-full ${getTaskStatusColor(row.newStatus as TaskStatus)}`}
                        >
                          {t(`newTaskStatus.${row.newStatus}`)}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-2 py-2 text-slate-900">{row?.description}</td>
                    <td className="whitespace-nowrap px-2 py-2">
                      {row?.matter_id ? (
                        <Link
                          to={`/app/matters/${row.matter_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-semibold text-[#0069D1] hover:underline"
                        >
                          {row?.matter_name ?? '—'}
                        </Link>
                      ) : (
                        <span className="font-semibold text-[#0069D1]">
                          {row?.matter_name ?? '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <Link
                        to={`/app/clients/${row?.client_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#0069D1] hover:underline"
                      >
                        {row?.client_name}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-slate-900">
                      <div className="font-semibold">{formatDueDate(row.dueAt)}</div>
                      <div className="text-[0.75rem] text-slate-400">
                        {formatDueTime(row.dueAt)}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <LeftBadge days={row?.left_days ?? 0} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The §3.1.1 personal blocks, ready to drop into a DraggableBlocksContainer.
 * `prefix` namespaces the ids so the MD dashboard's stored order can't collide
 * with a governance block of the same name.
 */
export function usePersonalBlocks(prefix = '') {
  const { t } = useTranslation('common');
  const id = (name: string) => (prefix ? `${prefix}-${name}` : name);

  return [
    {
      id: id('matters'),
      label: t('dashboard.myMatters'),
      content: <MyMattersCard />,
    },
    {
      id: id('calendar'),
      label: t('dashboard.meetings'),
      content: <MyMeetingsCard />,
    },
    {
      id: id('deadlines'),
      label: t('dashboard.deadlines'),
      content: <MyDeadlinesCard />,
    },
  ];
}
